/**
 * The proposal card's decisions (ai-plan-builder R15 to R18; boards S, S3): which changes stand
 * under which heading, which are drawn as one pair, which are outdated, what each toggle moves, and
 * the counts the header and the apply button state.
 *
 * Pure. The facts about the tree come from `entities/plan-draft` (`proposalFacts`,
 * `outdatedChanges`, the dependency closures); this module only arranges them. The words are
 * `lib/proposalWording.ts`'s.
 *
 * ── Grouping ─────────────────────────────────────────────────────────────────────────────────
 * A change to an exercise or a day stands under that day, in the plan's order; a new day stands
 * under „Nowy dzień" with the exercises on it; a change to a phase stands under „Struktura planu",
 * last. Changes that depend on each other (a new day and the exercises on it) are one PAIR: they are
 * accepted and rejected together (R15), so they are drawn together.
 *
 * ── A decision moves its whole group ─────────────────────────────────────────────────────────
 * Accepting a change accepts what it cannot be applied without (`withDependencies`); rejecting one
 * rejects what cannot be applied without it (`withDependents`). An outdated change takes no decision:
 * it can no longer be applied (R18), and nothing drawn offers one.
 */

import type { AiChangeDecision } from '~/entities/ai-conversation';
import {
  withDependencies,
  withDependents,
  type AiProposal,
  type AiProposalChange,
  type AiProposalChangeFacts,
  type AiProposalDay,
} from '~/entities/plan-draft';

import { exerciseDrawing, type ExerciseDrawing } from './exerciseDrawing';

/** The trainer's choice on a change before it is applied. */
export type ChangeChoice = 'accepted' | 'rejected';

export type ChangeChoices = ReadonlyMap<string, ChangeChoice>;

export interface ProposalRow {
  readonly facts: AiProposalChangeFacts;
  readonly outdated: boolean;
  /** Its result already stands in the plan, with no apply of it on record: nothing to decide. */
  readonly inPlan: boolean;
  readonly choice: ChangeChoice;
  /** How it is drawn as the builder's exercise card (board U); `null` when it keeps its line. */
  readonly drawing: ExerciseDrawing | null;
}

export type ProposalItem =
  | { readonly kind: 'row'; readonly row: ProposalRow }
  /** Changes that stand or fall together: a new day and the exercises on it. */
  | { readonly kind: 'pair'; readonly rows: readonly ProposalRow[] };

export type ProposalHeading =
  | { readonly kind: 'day'; readonly day: AiProposalDay }
  | { readonly kind: 'newDay' }
  | { readonly kind: 'structure' };

export interface ProposalGroup {
  readonly key: string;
  readonly heading: ProposalHeading;
  readonly items: readonly ProposalItem[];
}

export interface ProposalCardView {
  readonly groups: readonly ProposalGroup[];
  /** Changes the trainer can still decide on: every change neither outdated nor already in the plan. */
  readonly reviewable: number;
  /** What „Zastosuj" applies: the accepted changes neither outdated nor already in the plan. */
  readonly accepted: readonly string[];
  /** Some card marks sets (an alter or an addition drawn as the builder's card): the legend stands. */
  readonly marksSets: boolean;
}

/**
 * What the card opens with: every change accepted (the canon draws them so), unless the trainer
 * already decided on it; the server keeps the latest decision on each change (R53).
 */
export function initialChoices(proposal: AiProposal, recorded: ReadonlyMap<string, AiChangeDecision>): ChangeChoices {
  return new Map(
    proposal.changes.map((change): [string, ChangeChoice] => [change.id, recorded.get(change.id) === 'rejected' ? 'rejected' : 'accepted']),
  );
}

/**
 * The choices after the trainer accepts or rejects one change: its whole dependency group moves
 * with it. Answers the choices and the decisions to record — only what actually changed.
 */
export function choose(
  proposal: AiProposal,
  choices: ChangeChoices,
  changeId: string,
  choice: ChangeChoice,
  outdated: ReadonlySet<string>,
): { readonly choices: ChangeChoices; readonly decided: ReadonlyMap<string, ChangeChoice> } {
  const moved = choice === 'accepted' ? withDependencies(proposal.changes, [changeId]) : withDependents(proposal.changes, [changeId]);
  return chooseMany(choices, [...moved], choice, outdated);
}

/** „Przyjmij wszystkie" and „Odrzuć wszystko": every change that can still be decided. */
export function chooseAll(
  proposal: AiProposal,
  choices: ChangeChoices,
  choice: ChangeChoice,
  outdated: ReadonlySet<string>,
): { readonly choices: ChangeChoices; readonly decided: ReadonlyMap<string, ChangeChoice> } {
  return chooseMany(
    choices,
    proposal.changes.map((change) => change.id),
    choice,
    outdated,
  );
}

/**
 * „Przyjmij dzień" (R15): every change shown under that day's heading, with what each needs. An
 * outdated change is left as it is.
 */
export function chooseDay(
  proposal: AiProposal,
  choices: ChangeChoices,
  facts: readonly AiProposalChangeFacts[],
  daySlotToken: string,
  outdated: ReadonlySet<string>,
): { readonly choices: ChangeChoices; readonly decided: ReadonlyMap<string, ChangeChoice> } {
  const onDay = facts.filter((each) => each.day?.slotToken === daySlotToken && !outdated.has(each.change.id)).map((each) => each.change.id);
  return chooseMany(choices, [...withDependencies(proposal.changes, onDay)], 'accepted', outdated);
}

function chooseMany(
  choices: ChangeChoices,
  ids: readonly string[],
  choice: ChangeChoice,
  outdated: ReadonlySet<string>,
): { readonly choices: ChangeChoices; readonly decided: ReadonlyMap<string, ChangeChoice> } {
  const next = new Map(choices);
  const decided = new Map<string, ChangeChoice>();
  for (const id of ids) {
    if (outdated.has(id) || next.get(id) === choice) continue;
    next.set(id, choice);
    decided.set(id, choice);
  }
  return { choices: next, decided };
}

/** The changes that stand together, as groups of ids in the proposal's order: linked through `dependsOn`, either way. */
function linkedSets(changes: readonly AiProposalChange[]): ReadonlyMap<string, readonly string[]> {
  const neighbours = new Map<string, Set<string>>(changes.map((change) => [change.id, new Set<string>()]));
  for (const change of changes) {
    for (const dependency of change.dependsOn) {
      neighbours.get(change.id)?.add(dependency);
      neighbours.get(dependency)?.add(change.id);
    }
  }
  const byId = new Map<string, readonly string[]>();
  for (const change of changes) {
    if (byId.has(change.id)) continue;
    const reached = new Set<string>();
    const pending = [change.id];
    for (let id = pending.pop(); id !== undefined; id = pending.pop()) {
      if (reached.has(id)) continue;
      reached.add(id);
      pending.push(...(neighbours.get(id) ?? []));
    }
    const ordered = changes.map((each) => each.id).filter((id) => reached.has(id));
    for (const id of ordered) byId.set(id, ordered);
  }
  return byId;
}

function headingOf(facts: AiProposalChangeFacts): ProposalHeading {
  const { change } = facts;
  if (change.kind === 'phaseAdd' || change.kind === 'phaseRemove' || change.kind === 'phaseCycles') return { kind: 'structure' };
  if (facts.day === null) return { kind: 'structure' };
  return facts.day.added ? { kind: 'newDay' } : { kind: 'day', day: facts.day };
}

function headingKey(heading: ProposalHeading): string {
  return heading.kind === 'day' ? `day:${heading.day.slotToken}` : heading.kind;
}

function headingRank(heading: ProposalHeading): number {
  return heading.kind === 'day' ? 0 : heading.kind === 'newDay' ? 1 : 2;
}

/**
 * The card as it stands against the draft now. `facts` are `proposalFacts` over the same draft, in
 * the proposal's order; `present` is `presentChanges` over it; `dayOrder` is each existing day's
 * place in the plan, so the groups follow it.
 *
 * A change already in the plan (a redo put it back after the card's own undo, say) reads as such and
 * takes no decision: applying it again would write nothing. A removal whose target is gone reads as
 * outdated rather than in the plan: the trainer's edit wins (R18).
 */
export function proposalCardView(
  facts: readonly AiProposalChangeFacts[],
  outdated: ReadonlySet<string>,
  present: ReadonlySet<string>,
  choices: ChangeChoices,
  dayOrder: (slotToken: string) => number,
): ProposalCardView {
  const changes = facts.map((each) => each.change);
  const settled = (id: string) => outdated.has(id) || present.has(id);
  const linked = linkedSets(changes);
  const rowOf = (each: AiProposalChangeFacts): ProposalRow => ({
    facts: each,
    outdated: outdated.has(each.change.id),
    inPlan: present.has(each.change.id) && !outdated.has(each.change.id),
    choice: choices.get(each.change.id) ?? 'accepted',
    drawing: exerciseDrawing(each, outdated.has(each.change.id)),
  });

  const groups = new Map<string, { heading: ProposalHeading; items: ProposalItem[] }>();
  const placed = new Set<string>();
  for (const each of facts) {
    if (placed.has(each.change.id)) continue;
    const members = linked.get(each.change.id) ?? [each.change.id];
    const memberFacts = facts.filter((candidate) => members.includes(candidate.change.id));
    // A pair is headed by its new day when it has one, else by its first change.
    const lead = memberFacts.find((candidate) => candidate.change.kind === 'dayAdd') ?? each;
    const heading = headingOf(lead);
    const key = headingKey(heading);
    const group = groups.get(key) ?? { heading, items: [] };
    groups.set(key, group);
    group.items.push(memberFacts.length > 1 ? { kind: 'pair', rows: memberFacts.map(rowOf) } : { kind: 'row', row: rowOf(each) });
    for (const id of members) placed.add(id);
  }

  const ordered = [...groups.entries()]
    .map(([key, group]) => ({ key, heading: group.heading, items: group.items }))
    .sort((a, b) => {
      const rank = headingRank(a.heading) - headingRank(b.heading);
      if (rank !== 0) return rank;
      return a.heading.kind === 'day' && b.heading.kind === 'day' ? dayOrder(a.heading.day.slotToken) - dayOrder(b.heading.day.slotToken) : 0;
    });

  const marksSets = ordered
    .flatMap((group) => group.items.flatMap((item) => (item.kind === 'row' ? [item.row] : item.rows)))
    .some((row) => row.drawing !== null && row.drawing.baseline.kind !== 'none');
  return {
    groups: ordered,
    marksSets,
    reviewable: changes.filter((change) => !settled(change.id)).length,
    accepted: changes.filter((change) => !settled(change.id) && (choices.get(change.id) ?? 'accepted') === 'accepted').map((change) => change.id),
  };
}
